import { type FC, useMemo } from 'react';
import type { DisplayType } from '../types';
import {
  HistoricalYearsTooltip,
  type SalesSummaryProperties,
  SummaryHeader,
  SummaryTile,
  useImportMonth,
} from './index';

export const TransactionsSummary: FC<SalesSummaryProperties> = ({
  lastImported,
  thisYearSales,
  thisYearEstimates,
  historicalYears,
}) => {
  const displayType: DisplayType = 'Transactions';
  const thisYear = useMemo(() => thisYearSales.year, [thisYearSales]);
  const lastYear = useMemo(() => thisYear - 1, [thisYear]);
  const lastYearSales = useMemo(() => historicalYears[0], [historicalYears]);
  const { index, monthName, isMtdCurrent } = useImportMonth(lastImported);

  const historicalTooltipContent = useMemo(() => {
    if (historicalYears.length === 0) {
      return undefined;
    }

    return <HistoricalYearsTooltip historicalYears={historicalYears} displayType={displayType} />;
  }, [historicalYears]);

  return (
    <div className="grid grid-flow-row">
      <SummaryHeader>{monthName}</SummaryHeader>
      <div className="grid grid-flow-col">
        <SummaryTile
          title={`${lastYear} MTD`}
          current={lastYearSales?.totals_to_date_month.transactions}
          timespan="lastYear"
          format="number"
        />
        <SummaryTile
          title={`${thisYearSales.year} MTD`}
          current={isMtdCurrent ? thisYearSales.monthly[index].transactions : undefined}
          previous={isMtdCurrent ? lastYearSales?.totals_to_date_month.transactions : undefined}
          timespan="thisYear"
          format="number"
        />
        <SummaryTile
          title={`${thisYearSales.year} Total ~`}
          current={thisYearEstimates.monthly[index].transactions}
          timespan="thisYearEstimates"
          format="number"
        />
      </div>
      <SummaryHeader>Year</SummaryHeader>
      <div className="grid grid-flow-col">
        <SummaryTile
          title={`${lastYear} YTD`}
          current={lastYearSales?.totals_to_date.transactions}
          timespan="lastYear"
          format="number"
        />
        <SummaryTile
          title={`${thisYearSales.year} YTD`}
          current={thisYearSales.totals_to_date.transactions}
          previous={lastYearSales?.totals_to_date.transactions}
          timespan="thisYear"
          format="number"
          tooltipContent={historicalTooltipContent}
        />
        <SummaryTile
          title={`${thisYearSales.year} Total ~`}
          current={thisYearEstimates.totals.transactions}
          timespan="thisYearEstimates"
          format="number"
        />
      </div>
    </div>
  );
};
